import React from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import RequirementLink from './RequirementLink';
import { renderTextWithRequirementLinks } from '../../utils/requirementParser';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

/**
 * RequirementText component that automatically detects and renders requirement ID links
 */
const RequirementText = ({
  text,
  onRequirementClick,
  requirements = EMPTY_ARRAY,
  linkProps = EMPTY_OBJECT,
  ...props
}) => {
  if (!text || typeof text !== 'string') {
    return <span {...props}>{text}</span>;
  }

  const parts = renderTextWithRequirementLinks(text, onRequirementClick, linkProps);

  // `parts` is recomputed whole from `text` on every render (no add/remove/reorder UI,
  // no per-fragment local state), so a key only needs to stay unique within one render.
  // Built from each fragment's running offset in `text`, not the loop index the rule
  // flags, so two identical fragments at different offsets still get distinct keys.
  let offset = 0;
  return (
    <Box component="span" {...props}>
      {parts.map((part) => {
        if (typeof part === 'string') {
          const fragKey = `s-${offset}`;
          offset += part.length;
          return <span key={fragKey}>{part}</span>;
        }

        if (part.type === 'requirement-link') {
          const fragKey = `l-${offset}`;
          offset += String(part.id ?? '').length + 1;
          return (
            <RequirementLink
              key={fragKey}
              requirementId={part.id}
              onClick={part.props.onClick}
              requirements={requirements}
              {...part.props}
            />
          );
        }

        return null;
      })}
    </Box>
  );
};

RequirementText.propTypes = {
  text: PropTypes.string,
  onRequirementClick: PropTypes.func,
  requirements: PropTypes.array,
  linkProps: PropTypes.object
};

export default RequirementText;